// ============================================================
// CaseQuickViewModal.tsx
// Stage 1 of the case redesign: a hero-band QUICK-VIEW modal for a case,
// opened from a cases-list row. Same design family as the appointment/task
// modals (green hero-band, centered overlay, dark Option-2 tokens, view/edit
// split). It re-lays-out EXISTING case data — NO schema change.
//
//   • VIEW  — hero-band (PI Case pill + REF + Stage pill + title + subtitle +
//     a STAGE PROGRESS STRIP), an Overview tab (Client / Handling / Defendant-
//     TPI / Key dates), plus stubbed section tabs (Stage 3 builds them). Footer:
//     Close case…/Reopen… · Full case → · Edit. CLOSE = a close date → status
//     Closed (caseCloseActions.ts): a confirm with the close date (default
//     today) listing the case's OPEN deadlines — limitation ones first, in
//     amber (they keep alerting on a closed case) — each with an optional
//     "mark complete" tick (none ticked by default). REOPEN clears the date
//     (alerts resume). The case PUT audits case.closed / case.reopened and
//     allows only an admin, the owner or the handling team.
//   • EDIT  — the same band + a tidy form for the safely-editable case fields
//     (title, date of incident, defendant, fee-earner, status) wired to the
//     EXISTING updateCaseApi / updateCaseAssigneeApi. Gated to admin-or-assignee.
//
// ★ STAGE STRIP DEPENDENCY: the Case model has NO stage field yet (Callum's PI
// stages are pending). Until then the strip + pill DEGRADE GRACEFULLY to the
// derived case STATUS (Draft → Open → Closed). When a real stage enum lands,
// swap `deriveStage` for it and the strip renders the true PI journey.
//
// "Full case →" opens the existing full case page (onOpenFull → the list's
// handleOpenCase) — the full-case flow is untouched; this only ADDS a glance.
// ============================================================
import { serverErrorMessage } from '../../utils/serverError'
import { caseDobOf } from '../../utils/caseDob'
import { useLivePermissions } from '../../hooks/useLivePermissions'
import { BTN_PRIMARY_MD } from '../../styles/buttons'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CaseSummary } from '../layout/CaseListScreen'
import type { RawCase } from '../../types/case.types'
import { caseName as sharedCaseName, caseClientName, caseRef, NEW_CASE_NAME } from '../../utils/caseName'
import type { CaseDeadline } from '../../types/deadlines.types'
import { fetchCaseByIdShared, listCaseDeadlinesShared, updateCaseApi, caseOpenErrorMessage } from '../../api/casevoApi'
import AudienceControl, { AssigneeChips, Audience, audienceFrom, audiencePayload } from './AudienceControl'
import { saveCaseAssignment, assignmentErrorMessage } from './caseAssignment'
import { useCasevoStore } from '../../store/useCasevoStore'
import { formatDateUK } from '../../utils/caseHelpers'
import { limitationRowOf } from '../case/caseFacts'
import UserAvatar from './UserAvatar'
import { ErrorNote } from './ActionBox'
import DateInput from './DateInput'
import { ymdLocal } from '../tasks/myWorkLogic'
import { useLiveUserName } from '../../hooks/useLiveUserName'
import { listActivitiesApi, deleteActivityApi } from '../../api/casevoApi'
import { useLoadPhase, SKELETON_BAR } from '../../hooks/useLoadPhase'
import { presence } from '../../services/presence'
import type { Activity } from '../../types/activity.types'
import { entryTypeFor, TONES, resolveEntryTone } from '../../constants/entryTypes'
import { relativeTime } from '../../utils/relativeTime'
import { useActivityView } from './ActivityViewContent'
import { EntryIconSvg } from '../clients/activityIcons'
import { HERO_PILL } from './ModalFrame'
import AddActivityModal from './AddActivityModal'
import ConfirmDialogue from './ConfirmDialogue'
import { closeCase, closeDialogDeadlines, reopenCase } from './caseCloseActions'
import { estimatedLimitation, estimateParen, limitationDateCheck, LIMITATION_NOTES, noTimeLimitApplies } from '../../utils/limitationRules'
import LaterThanEstimateDot from '../case/LaterThanEstimateDot'
import { claimFactsOf, claimOfRaw } from '../../utils/caseClaim'
import { claimTypeLabel } from '../../utils/claimTypes'
import { accidentDateError, accidentDateWarning, closeDateError } from '../../utils/caseDateRules'
import { londonToday } from '../../utils/civilDate'
import { useDraftDirty, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'

interface CaseQuickViewModalProps {
  caseSummary: CaseSummary
  onClose: () => void
  // Open the existing FULL case page. Hands back the already-fetched full row
  // + deadlines so the caller can (a) skip the full-case getCaseById round-trip
  // and (b) cache them to re-seed this modal instantly on Back.
  onOpenFull: (id: string, full: RawCase | null, deadlines: CaseDeadline[] | null) => void
  // Client click-through (the list's openClientDetails takes the row).
  onOpenClient: (c: CaseSummary) => void
  // Sync the list row after an in-modal edit (no re-fetch of the whole list).
  onPatched: (id: string, patch: Partial<CaseSummary>) => void
  // Seed data (Back re-open): the full row + deadlines we already had, so the
  // re-opened modal shows content IMMEDIATELY (no empty/loading beat). It still
  // refreshes in the background. Omitted on a fresh row-click open.
  initialFull?: RawCase | null
  initialDeadlines?: CaseDeadline[] | null
}

// ── Inline line icons (Feather-style, currentColor) — no emojis, no lib.
// Mirrors the appointment/task modal icon set so the three share a family.
const IcBase: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className ?? 'w-4 h-4 inline-block align-[-3px] shrink-0'} aria-hidden="true">{children}</svg>
)
const IcFile: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></IcBase>
const IcUser: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></IcBase>
const IcUsers: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></IcBase>
const IcShield: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></IcBase>
const IcClock: React.FC<{ className?: string }> = (p) => <IcBase {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></IcBase>
const IcActivity: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></IcBase>
const IcArrowLeft: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="M19 12H5M12 19l-7-7 7-7" /></IcBase>
const IcChevLeft: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="m15 18-6-6 6-6" /></IcBase>
const IcChevRight: React.FC<{ className?: string }> = (p) => <IcBase {...p}><path d="m9 18 6-6-6-6" /></IcBase>

// Recent activity: a glance, not a feed — hard cap so the modal stays balanced.
const RECENT_ACTIVITY_LIMIT = 4
// One recent-activity row: py-1 (8px) + 18px title line + 16px meta line.
const ACTIVITY_ROW_PX = 42
// One rail row: the 2px rail runs down the left (::before), trimmed to start /
// end at the first / last dot so it never dangles past the list.
const RAIL_ROW =
  "relative min-w-0 -mx-2 pl-[30px] before:content-[''] before:absolute before:left-[11px] before:top-0 before:bottom-0 before:w-[2px] before:bg-casevo-border-soft " +
  'first:before:top-[13px] last:before:bottom-auto last:before:h-[13px] only:before:hidden'
// Hover highlight as its own OPAQUE layer (::after, z-1) so it covers the rail
// (::before) inside the hovered row — the row's own background can't, since a
// pseudo-element always paints above it. Dot + text sit on top at z-2.
const RAIL_HOVER =
  "after:content-[''] after:absolute after:inset-0 after:z-[1] after:rounded-lg after:bg-casevo-input " +
  'after:opacity-0 hover:after:opacity-100 after:transition-opacity'
// Drill-in motion (row → activity detail, back, ‹ › steps): the incoming
// content slides SHIFT px in from the travel side and fades up while the
// shell's height eases between the two states.
const DRILL_MS = 280
const DRILL_SHIFT = 28
const DRILL_EASE = 'cubic-bezier(.2,.8,.2,1)'
// Header stepper ‹ › — white-alpha on the hero band, like the ✕.
const STEP_BTN =
  'w-[26px] h-[26px] rounded-[7px] bg-white/[0.12] hover:bg-white/25 text-white grid place-items-center transition-colors focus:outline-none casevo-focus disabled:opacity-[.35] disabled:hover:bg-white/[0.12] disabled:cursor-default'

const inputCls =
  'w-full border border-casevo-border rounded-lg px-3 py-2.5 text-sm bg-casevo-input dark:bg-[#1f2e26] text-casevo-forest focus:outline-none focus:border-casevo-mid'
const lblCls = 'block text-[11px] font-semibold uppercase tracking-wide text-casevo-mid mb-1.5'
const bhCls = 'flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-casevo-mid mb-3 pb-2 border-b border-casevo-border'
const kLbl = 'text-[11px] font-semibold text-casevo-mid mb-0.5'
const kVal = 'text-sm font-medium text-casevo-forest'
// A field value's placeholder: the bar sits INSIDE the real value wrapper
// (kVal → the same 20px line), so swapping in the value is height-neutral.
// Invisible during the first 250ms ('wait'), a pulsing bar after that.
const ValueSkel: React.FC<{ w: string; phase: 'wait' | 'skeleton' | 'ready' }> = ({ w, phase }) => (
  <div className={kVal} aria-hidden="true"><span className={`${SKELETON_BAR} h-3 ${w} ${phase === 'wait' ? 'invisible' : ''}`} /></div>
)

type Stage = 'draft' | 'open' | 'closed'
const STAGE_ORDER: Stage[] = ['draft', 'open', 'closed']
const STAGE_LABEL: Record<Stage, string> = { draft: 'Draft', open: 'Open', closed: 'Closed' }
// ★ Placeholder stage = derived case STATUS (no real stage field yet).
const deriveStage = (closeDate: string | null, setupFinalised: boolean): Stage =>
  // A close date of only spaces means OPEN (same as the server's isCaseClosed).
  closeDate && closeDate.trim() ? 'closed' : setupFinalised ? 'open' : 'draft'


const CaseQuickViewModal: React.FC<CaseQuickViewModalProps> = ({
  caseSummary,
  onClose,
  onOpenFull,
  onOpenClient,
  onPatched,
  initialFull = null,
  initialDeadlines = null,
}) => {
  const authUser = useCasevoStore((s) => s.authUser)
  const currentUserId = authUser?.id ?? ''
  const isAdmin = authUser?.role === 'admin'
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  const loadFirmUsers = useCasevoStore((s) => s.loadFirmUsers)
  const getFirmUserById = useCasevoStore((s) => s.getFirmUserById)
  // ★ Live names by id (central firmUsers/authUser, patched on rename).
  const liveName = useLiveUserName()
  useEffect(() => { void loadFirmUsers() }, [loadFirmUsers])

  const [view, setView] = useState<'view' | 'edit'>('view')
  // Full case row (defendant/TPI/openDate) + deadlines, fetched on open. The
  // band + basic overview render immediately from the list row; the fetch
  // enriches Defendant/TPI + Key dates.
  // Seed from initialFull/initialDeadlines when re-opened on Back so content
  // shows on the first paint (no empty/loading beat); still refreshed below.
  const [full, setFull] = useState<RawCase | null>(initialFull)
  // ★ (case-page audit) Set once an in-modal save lands: `full` is then only
  // partly patched (name / stage / team aren't written back into it), so
  // "Full case →" must NOT hand it to the full page as a prefetched row — it
  // would paint (and seed the autosave baseline from) the pre-edit case.
  const editedRef = useRef(false)
  const [deadlines, setDeadlines] = useState<CaseDeadline[] | null>(initialDeadlines)
  const [detailsLoading, setDetailsLoading] = useState(initialFull == null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Footer Archive / Reopen (view mode): which confirm is open + its close date.
  const [statusDialog, setStatusDialog] = useState<'close' | 'reopen' | null>(null)
  const [closeDateDraft, setCloseDateDraft] = useState<string | null>(null)
  // Half-typed dates hold Save / Close (DateInput keeps the old value and says
  // "Finish the date or clear it" — nothing half-typed is ever sent).
  const [accidentTyping, setAccidentTyping] = useState(false)
  const [closeTyping, setCloseTyping] = useState(false)
  // Deadlines ticked to be marked complete as part of closing (none by default).
  const [completeOnClose, setCompleteOnClose] = useState<string[]>([])
  const [statusBusy, setStatusBusy] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)
  // Live status source — starts from the seed row (full when re-opened, else
  // the list summary), advanced by an in-modal status edit.
  const [closeDate, setCloseDate] = useState<string | null>(initialFull?.closeDate ?? caseSummary.closeDate)
  const [setupFinalised, setSetupFinalised] = useState<boolean>(initialFull?.setupFinalised ?? caseSummary.setupFinalised)
  const [caseName, setCaseName] = useState<string | null>(initialFull?.caseName ?? caseSummary.caseName ?? null)
  // Saved defendant — seeded from the list summary (getCases now carries it),
  // so the title is FINAL on first paint (no client-name → "v" flash).
  const [defendant, setDefendant] = useState<string>(initialFull?.defendantName ?? caseSummary.defendantName ?? '')

  // Edit-form local state (prefer the seeded full row, else the list summary).
  const [formTitle, setFormTitle] = useState(initialFull?.caseName ?? caseSummary.caseName ?? '')
  const [formAccidentDate, setFormAccidentDate] = useState<string | null>(initialFull?.accidentDate ?? caseSummary.accidentDate)
  const [formDefendant, setFormDefendant] = useState(initialFull?.defendantName ?? caseSummary.defendantName ?? '')
  const [formStage, setFormStage] = useState<Stage>(deriveStage(initialFull?.closeDate ?? caseSummary.closeDate, initialFull?.setupFinalised ?? caseSummary.setupFinalised))
  const [formAssignee, setFormAssignee] = useState<string>(initialFull?.assignedToUserId ?? caseSummary.assignedToUserId ?? '')
  // ★ AUDIENCE / handling team (multi-assignee batch) — the appointment control.
  // Seeded from the richest row we have; refreshed from the authoritative fetch.
  const seedTeamIds = (initialFull?.assignees ?? caseSummary.assignees ?? []).map((a) => a.userId)
  const seedFirmWide = initialFull?.firmWide ?? caseSummary.firmWide ?? false
  const [teamIds, setTeamIds] = useState<string[]>(seedTeamIds)          // saved (view)
  const [teamFirmWide, setTeamFirmWide] = useState<boolean>(seedFirmWide) // saved (view)
  const [formAudience, setFormAudience] = useState<Audience>(audienceFrom(seedFirmWide, seedTeamIds))
  const [formAudienceIds, setFormAudienceIds] = useState<string[]>(seedTeamIds)
  // Unsaved-changes guard while editing: ×, Cancel, Back or leaving the
  // screen ask before dropping the edit.
  const editDirty = useDraftDirty(view === 'edit', [formTitle, formAccidentDate, formDefendant, formStage, formAssignee, formAudience, formAudienceIds])
  const guardEdit = useUnsavedChangesGuard(editDirty && !saving)
  const canAssignCasesToOthers = authUser?.effectivePermissions?.assignCasesToOthers ?? false
  const canCreateFirmWideCases = authUser?.effectivePermissions?.createFirmWideCases ?? false

  useEffect(() => {
    let cancelled = false
    // Only flip to the loading state when we have nothing seeded — a re-open
    // with initialFull refreshes SILENTLY (no "Loading…" flash over content).
    if (!initialFull) setDetailsLoading(true)
    void Promise.all([fetchCaseByIdShared(caseSummary.id), listCaseDeadlinesShared(caseSummary.id)])
      .then(([raw, dls]) => {
        if (cancelled) return
        setFull(raw)
        setDeadlines(dls)
        // Refresh the band/status source from the AUTHORITATIVE full row so a
        // re-opened modal (Back from the full page) reflects any rename / status
        // change made there — the list-row summary that seeded us may be stale.
        setCaseName(raw.caseName ?? null)
        setCloseDate(raw.closeDate)
        setSetupFinalised(raw.setupFinalised)
        setFormTitle(raw.caseName ?? '')
        setFormStage(deriveStage(raw.closeDate, raw.setupFinalised))
        setFormAccidentDate(raw.accidentDate ?? caseSummary.accidentDate)
        setDefendant(raw.defendantName ?? '')
        setFormDefendant(raw.defendantName ?? '')
        setFormAssignee(raw.assignedToUserId ?? '')
        {
          const ids = (raw.assignees ?? []).map((a) => a.userId)
          setTeamIds(ids)
          setTeamFirmWide(raw.firmWide ?? false)
          setFormAudience(audienceFrom(raw.firmWide, ids))
          setFormAudienceIds(ids)
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[CaseQuickViewModal] Failed to load case details:', err)
        // Access-denied / deleted → the clean message; else the generic one.
        if (!cancelled) setError(caseOpenErrorMessage(err) ?? 'Could not load the full case details.')
      })
      .finally(() => { if (!cancelled) setDetailsLoading(false) })
    return () => { cancelled = true }
    // Open-scoped fetch: runs when the case changes. initialFull is only read
    // to decide the loading flash and is stable per open — excluded on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseSummary.id, caseSummary.accidentDate])

  // ── Recent activity ───────────────────────────────────────
  // Same case-scoped source as the case Activity section (listActivitiesApi
  // { clientId, caseId } — newest first, server-side visibility). The API has
  // no limit param, so the cap is applied here. A failure stays local to this
  // block (quiet line), never the modal's error banner.
  const recentClientId = caseSummary.client?.id ?? null
  const [recent, setRecent] = useState<Activity[] | null>(null)
  const [recentError, setRecentError] = useState(false)
  const [recentReload, setRecentReload] = useState(0)
  // ★ Live permission change: clear the recent entries' £ at once if a money
  // right was lost, then re-read.
  useLivePermissions((narrowed) => { if (narrowed) setRecent(null); setRecentReload((n) => n + 1) })
  // Blank to the skeleton only when the CASE changes — a reload after an
  // edit / delete / re-price keeps the list (and an open drill-in) on screen.
  const recentKeyRef = useRef<string | null>(null)
  useEffect(() => {
    let cancelled = false
    const key = `${caseSummary.id}|${recentClientId ?? ''}`
    if (recentKeyRef.current !== key) { recentKeyRef.current = key; setRecent(null) }
    setRecentError(false)
    if (!recentClientId) { setRecent([]); return }
    listActivitiesApi({ clientId: recentClientId, caseId: caseSummary.id })
      .then(({ activities }) => { if (!cancelled) setRecent(activities.slice(0, RECENT_ACTIVITY_LIMIT)) })
      .catch(() => { if (!cancelled) setRecentError(true) })
    return () => { cancelled = true }
  }, [caseSummary.id, recentClientId, recentReload])
  // Live: a colleague's activity create / update / delete on THIS case (and a
  // socket RE-connect — events sent while it was down are lost) → the same
  // refetch via recentReload, debounced like CaseActivitySection. The list and
  // an open drill-in stay on screen; a remotely deleted open entry drops back
  // to the case view (the drill-in effect below). Own changes are echo-
  // suppressed upstream (presence) and already reload here.
  useEffect(() => {
    if (!recentClientId) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const scheduleReload = (): void => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => { timer = null; setRecentReload((n) => n + 1) }, 400)
    }
    const offs = [
      presence.subscribeEntityChanged((e) => {
        if (e.entity !== 'activity') return
        if (e.scope?.clientId !== recentClientId || e.scope?.caseId !== caseSummary.id) return
        scheduleReload()
      }),
      presence.subscribeReconnected(scheduleReload),
    ]
    return () => { offs.forEach((off) => off()); if (timer) clearTimeout(timer) }
  }, [caseSummary.id, recentClientId])
  // ★ NO LAYOUT SHIFT (hooks/useLoadPhase). The details (+ deadlines) and the
  // recent activity load in PARALLEL on open; the late blocks (Defendant /
  // TPI, Key dates, Recent activity) swap from their same-shape placeholders
  // to real content TOGETHER, once both have answered — invisible for the
  // first 250ms (still holding the space), a skeleton after that. Client and
  // Handling team come from the list row and render at once.
  const lateReady = (!detailsLoading || !!error) && (recent !== null || recentError)
  const latePhase = useLoadPhase(lateReady)
  // Row click → DRILL-IN: this same frame swaps in place to the activity's
  // detail (the real ActivityViewModal content via useActivityView), under a
  // breadcrumb header (← · case › Recent activity · ‹ n of 4 ›). ← / the case
  // name / Esc go back (focus returns to the row); a second Esc closes. The
  // stepper walks the loaded recent entries; "View all activity" remains the
  // route to the full history. Edit / Delete are the case Activity section's
  // wiring: AddActivityModal (edit mode) stacked on top / ConfirmDialogue.
  const [drillId, setDrillId] = useState<string | null>(null)
  const drillIdx = drillId && recent ? recent.findIndex((a) => a.id === drillId) : -1
  const drillActivity = drillIdx >= 0 && recent ? recent[drillIdx] : null
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null)
  const [confirmDeleteActivity, setConfirmDeleteActivity] = useState<Activity | null>(null)
  const [deletingActivity, setDeletingActivity] = useState(false)
  const [drillError, setDrillError] = useState<string | null>(null)
  // The entry left the loaded list (deleted elsewhere / pushed out) → back.
  useEffect(() => { if (drillId && recent && !recent.some((a) => a.id === drillId)) setDrillId(null) }, [drillId, recent])
  const shellRef = useRef<HTMLDivElement>(null)
  const drillBackRef = useRef<HTMLButtonElement>(null)
  const rowRefs = useRef(new Map<string, HTMLLIElement>())
  // Set by navigate(), consumed by the layout effect below (one transition).
  const nav = useRef<{ dir: 1 | -1; fromHeight: number | null; focus: 'back' | string | null } | null>(null)
  const navigate = (nextId: string | null, dir: 1 | -1): void => {
    nav.current = {
      dir,
      fromHeight: shellRef.current ? shellRef.current.getBoundingClientRect().height : null,
      // Into a detail → its ← button; back to the case → the entry's row.
      focus: nextId ? 'back' : drillId,
    }
    setDrillError(null)
    setDrillId(nextId)
  }
  const backToCase = (): void => navigate(null, -1)
  const stepDrill = (d: 1 | -1): void => {
    const next = drillIdx >= 0 && recent ? recent[drillIdx + d] : undefined
    if (next) navigate(next.id, d)
  }
  useLayoutEffect(() => {
    const n = nav.current
    if (!n) return
    nav.current = null
    if (n.focus === 'back') drillBackRef.current?.focus()
    else if (n.focus) rowRefs.current.get(n.focus)?.focus()
    const shell = shellRef.current
    if (!shell || typeof shell.animate !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    shell.querySelectorAll<HTMLElement>('[data-drill-pane]').forEach((el) => {
      el.animate(
        [{ opacity: 0, transform: `translateX(${n.dir * DRILL_SHIFT}px)` }, { opacity: 1, transform: 'none' }],
        { duration: DRILL_MS, easing: DRILL_EASE },
      )
    })
    const to = shell.getBoundingClientRect().height
    if (n.fromHeight != null && Math.abs(to - n.fromHeight) > 1) {
      shell.animate([{ height: `${n.fromHeight}px` }, { height: `${to}px` }], { duration: DRILL_MS, easing: DRILL_EASE })
    }
  }, [drillId])
  const handleConfirmDeleteActivity = async (): Promise<void> => {
    const target = confirmDeleteActivity
    if (!target) return
    setDeletingActivity(true)
    try {
      await deleteActivityApi(target.id)
      setConfirmDeleteActivity(null)
      if (drillId === target.id) navigate(null, -1)
      setRecent((r) => (r ? r.filter((a) => a.id !== target.id) : r))
      setRecentReload((n) => n + 1) // refill the 4th slot
    } catch (err) {
      setConfirmDeleteActivity(null)
      setDrillError(err instanceof Error ? err.message : 'Failed to delete activity')
    } finally {
      setDeletingActivity(false)
    }
  }
  const canModifyActivity = (a: Activity): boolean => authUser !== null && (isAdmin || authUser.id === a.actorUserId)
  const firmColourScheme = useCasevoStore((s) => s.firmColourScheme)
  const userColourScheme = useCasevoStore((s) => s.userColourScheme)
  // "View all activity" → this case's CLIENT Activity tab, case filter
  // pre-applied (one-shot store intents read by ClientSearchView), then the
  // caller's usual open-client path.
  const viewAllActivity = (): void => {
    if (!recentClientId) return
    const store = useCasevoStore.getState()
    store.setPendingClientSection('activity')
    store.setPendingClientActivityCase({ clientId: recentClientId, caseId: caseSummary.id })
    onOpenClient(caseSummary)
  }

  // ── Derived display ───────────────────────────────────────
  const stage = deriveStage(closeDate, setupFinalised)
  const openDeadlinesForClose = useMemo(() => closeDialogDeadlines(deadlines), [deadlines])
  // The same date rules as the full case page (utils/caseDateRules; the server
  // enforces the blocks too).
  const todayUk = londonToday()
  // THE DOB this case uses (utils/caseDob): its own copy, else the client record.
  // The list row has no client-record DOB, so prefer the full case (its own
  // copy + the linked client) and fall back to the row's copy while it loads.
  const dobInfo = caseDobOf(full?.clientDateOfBirth ?? caseSummary.clientDateOfBirth, full?.client?.dateOfBirth ?? caseSummary.client?.dateOfBirth)
  const caseDob = dobInfo.dob
  // (Audit fix 1) Both dates of birth when they differ → the earlier estimate.
  const otherDob = dobInfo.otherDob
  const accidentBlock = accidentDateError(formAccidentDate, caseDob, todayUk)
  const accidentWarn = accidentDateWarning(formAccidentDate, todayUk)
  const closeBlock = closeDateError(closeDateDraft, full?.openDate ?? caseSummary.openDate ?? null, todayUk)
  const stageIdx = STAGE_ORDER.indexOf(stage)
  const ref = caseRef(caseSummary) // THE reference (SURNAME-YEAR-ID)
  const clientName = useMemo(() => caseClientName(caseSummary) || NEW_CASE_NAME, [caseSummary])
  const defendantName = defendant.trim()
  // THE case name — shared rule (utils/caseName.ts), same as the card.
  const title = sharedCaseName({ ...caseSummary, caseName, defendantName })
  // What the title falls back to when the custom name is cleared (edit placeholder).
  const autoTitle = sharedCaseName({ ...caseSummary, caseName: null, defendantName })
  const instructedIso = caseSummary.openDate ?? caseSummary.createdAt
  const accidentIso = full?.accidentDate ?? caseSummary.accidentDate
  // The claim facts the estimate reads (B2) — from the full case when loaded.
  const claimFacts = useMemo(() => claimFactsOf(claimOfRaw(full ?? caseSummary), full?.piMatter?.protectedParty), [full, caseSummary])
  const assignee = full?.assignedTo ?? caseSummary.assignedTo ?? null
  // (B2) The case's real claim type (full name; the card shows the short one).
  // null = not set (older cases).
  const claimTypeName = claimTypeLabel(full?.claimType ?? caseSummary.claimType)

  // The drill-in's content — the SAME view ActivityViewModal renders (one
  // source: ActivityViewContent). Its footer Close closes the quick-view, like
  // the ✕; ← / the breadcrumb / Esc are the way back to the case.
  const drillView = useActivityView({
    isOpen: drillActivity !== null,
    activity: drillActivity,
    onClose,
    isAdmin,
    canModify: !!drillActivity && canModifyActivity(drillActivity),
    onEdit: (a) => setEditingActivity(a),
    onDelete: (a) => { setDrillError(null); setConfirmDeleteActivity(a) },
    onRepriced: () => setRecentReload((n) => n + 1),
    caseLabel: { name: title, ref },
  })

  // Keys (view mode): Esc → back from a drill-in, else close; ← / → step the
  // drill-in. A dialog stacked on top (edit / delete / re-price confirm) owns
  // the keys, and so do text fields.
  useEffect(() => {
    if (view !== 'view') return
    const onKey = (e: KeyboardEvent): void => {
      if (e.defaultPrevented || document.querySelector('[aria-modal="true"]')) return
      if (e.key === 'Escape') {
        e.preventDefault()
        if (drillId) backToCase()
        else onClose()
        return
      }
      if (!drillId || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return
      const target = e.target instanceof Element ? e.target : null
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
      e.preventDefault()
      stepDrill(e.key === 'ArrowLeft' ? -1 : 1)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  // Stage pill colour by status (mirrors the task/appointment pill palette).
  const stagePillCls = stage === 'closed'
    ? 'bg-white/20 text-white'
    : stage === 'open' ? 'bg-[#74c69d] text-[#0d2a1a]' : 'bg-[#e9a23b] text-[#3a2a08]'

  // Key dates from real deadlines. Limitation = the "Limitation date" deadline
  // if present, else THE estimate (utils/limitationRules: a child at the
  // accident runs from their 18th birthday; no DOB → accident + 3 years,
  // labelled so). Pure civil-date arithmetic; a 29 Feb anniversary that
  // doesn't exist clamps to 28 Feb (the EARLIER — safe — side).
  const limitationText = useMemo(() => {
    // Same row rule as the hero / Overview / Deadlines (caseFacts.limitationRowOf).
    const lim = limitationRowOf(deadlines ?? undefined)
    if (lim) return formatDateUK(lim.dueDate.slice(0, 10))
    const est = estimatedLimitation(accidentIso, { dob: caseDob, otherDob }, claimFacts)
    if (!est && !claimFacts.overrideDate && noTimeLimitApplies(accidentIso, { dob: caseDob, otherDob }, claimFacts)) return 'No time limit (s.11ZA, confirmed)'
    return est ? `${formatDateUK(est.ymd)} ${estimateParen(est.basis)}` : '—'
  }, [deadlines, accidentIso, caseDob, otherDob, claimFacts])
  // The shared limitation checks on a SET limitation deadline (warnings; the
  // before-the-accident case is blocked by the server) — same as Deadlines.
  const limitationCheck = useMemo(() => {
    const lim = limitationRowOf(deadlines ?? undefined)
    if (!lim || lim.completed) return null
    return limitationDateCheck(lim.dueDate.slice(0, 10), accidentIso, { dob: caseDob, otherDob }, claimFacts)
  }, [deadlines, accidentIso, caseDob, otherDob, claimFacts])
  const nextDeadline = useMemo(() => {
    if (!deadlines) return null
    const upcoming = deadlines
      .filter((d) => !d.completed && d.dueDate)
      .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))
    return upcoming[0] ?? null
  }, [deadlines])
  const nextDeadlineUrgent = useMemo(() => {
    if (!nextDeadline) return false
    const due = new Date(nextDeadline.dueDate).getTime()
    if (Number.isNaN(due)) return false
    return due < Date.now() + 14 * 24 * 3600 * 1000 // overdue or within 14 days
  }, [nextDeadline])

  const clientId = caseSummary.client?.id ?? null
  // Owner OR a handling-team member (CaseAssignee) may edit — same people who
  // now see the case via the audience rule. Admins always can.
  const canEdit = isAdmin
    || (caseSummary.assignedToUserId !== null && caseSummary.assignedToUserId === currentUserId)
    || teamIds.includes(currentUserId)

  // ── Archive / Reopen (view-mode footer) ───────────────────
  const runStatus = async (kind: 'close' | 'reopen'): Promise<void> => {
    if (kind === 'close' && !closeDateDraft) return
    setStatusBusy(true); setStatusError(null)
    try {
      let patch: { closeDate: string | null }
      if (kind === 'close') {
        const r = await closeCase(caseSummary.id, closeDateDraft!, completeOnClose)
        patch = r.patch
        if (r.completed.length) setDeadlines((ds) => (ds ? ds.map((d) => (r.completed.includes(d.id) ? { ...d, completed: true } : d)) : ds))
        // Closed, but some ticked deadlines couldn't be completed — say so.
        if (r.failed.length) setStatusError(`Case closed, but ${r.failed.length === 1 ? 'one deadline' : `${r.failed.length} deadlines`} couldn’t be marked complete — mark ${r.failed.length === 1 ? 'it' : 'them'} on the Deadlines page.${r.failedWhy ? ` ${r.failedWhy}` : ''}`)
      } else {
        patch = await reopenCase(caseSummary.id)
      }
      setCloseDate(patch.closeDate)
      editedRef.current = true
      onPatched(caseSummary.id, patch)
      setStatusDialog(null)
    } catch (err) {
      setStatusDialog(null)
      // The server's own reason for a 400 / 403 (e.g. a close date before the
      // open date), else the shared wording, else a generic line.
      setStatusError(serverErrorMessage(err, [400, 403]) || caseOpenErrorMessage(err) || (kind === 'close' ? 'Couldn’t close the case. Please try again.' : 'Couldn’t reopen the case. Please try again.'))
    } finally { setStatusBusy(false) }
  }

  // ── Save (edit) ───────────────────────────────────────────
  const handleSave = async (): Promise<void> => {
    setSaving(true)
    setError(null)
    try {
      // Map the stage select → the derived status source columns.
      const stagePatch =
        formStage === 'closed'
          ? { closeDate: (closeDate && closeDate.trim()) ? closeDate : ymdLocal(new Date()), setupFinalised: true } // LOCAL today (UTC slice recorded yesterday 00:00–01:00 BST)
          : formStage === 'open'
          ? { closeDate: null, setupFinalised: true }
          : { closeDate: null, setupFinalised: false }
      const nextName = formTitle.trim() ? formTitle.trim() : null
      const casePatch: Record<string, unknown> = {
        caseName: nextName,
        accidentDate: formAccidentDate,
        defendantName: formDefendant.trim(),
        ...stagePatch,
      }
      await updateCaseApi(caseSummary.id, casePatch)
      // Owner + audience → the SHARED assignment save path (same as the case
      // list's right-click "Assign to"): owner via the assignee endpoint,
      // audience via the diff-gated case update, each skipped when unchanged.
      const result = await saveCaseAssignment(
        {
          id: caseSummary.id,
          assignedToUserId: caseSummary.assignedToUserId ?? null,
          firmWide: teamFirmWide,
          assignees: teamIds.map((userId) => ({ userId })),
        },
        {
          ownerId: formAssignee || null,
          audience: audiencePayload(formAudience, formAudienceIds, canAssignCasesToOthers, canCreateFirmWideCases),
        },
      )
      const nextAssignee = result.assignedToUserId
      const savedIds = result.assigneeIds
      const savedFirmWide = result.firmWide
      setTeamIds(savedIds)
      setTeamFirmWide(savedFirmWide)
      // Reflect locally (band/strip/overview) without a re-fetch.
      setCaseName(nextName)
      setCloseDate(stagePatch.closeDate)
      setSetupFinalised(stagePatch.setupFinalised)
      setDefendant(formDefendant.trim())
      setFull((prev) => (prev ? { ...prev, accidentDate: formAccidentDate, defendantName: formDefendant.trim() } : prev))
      editedRef.current = true
      // Sync the list row.
      const u = nextAssignee ? getFirmUserById(nextAssignee) : null
      onPatched(caseSummary.id, {
        caseName: nextName,
        defendantName: formDefendant.trim(),
        accidentDate: formAccidentDate,
        closeDate: stagePatch.closeDate,
        setupFinalised: stagePatch.setupFinalised,
        assignedToUserId: nextAssignee,
        assignedTo: u ? { id: u.id, firstName: u.firstName, lastName: u.lastName } : null,
        firmWide: savedFirmWide,
        assignees: savedIds.map((userId) => ({ userId })),
      })
      setView('view')
    } catch (err) {
      // Shared wording; surfaces the backend's permission message verbatim.
      // A refused field (400, e.g. an accident date before the DOB) gets the
      // server's own message, not the assignment wording.
      setError(serverErrorMessage(err, [400]) ?? assignmentErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  // ── Shared hero band (view + edit) ────────────────────────
  const heroBand = (editable: boolean): React.ReactNode => (
    <div className="relative px-6 sm:px-8 py-6 text-white bg-gradient-to-br from-casevo-forest to-casevo-brand dark:from-[#1a4230] dark:to-[#2d6a4f]">
      <button type="button" onClick={() => guardEdit(onClose)} aria-label="Close"
        className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors focus:outline-none casevo-focus">✕</button>
      <div data-drill-pane="">
      <div className="flex items-center gap-2 mb-3 flex-wrap pr-12">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md bg-white/20">
          <IcFile className="w-3.5 h-3.5" />{editable ? 'Edit case' : 'PI Case'}
        </span>
        <span className="inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-md bg-white/10 font-mono">REF {ref}</span>
        {!editable && (
          <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-md ${stagePillCls}`}>
            Stage: {STAGE_LABEL[stage]}
          </span>
        )}
      </div>
      {editable ? (
        <input
          value={formTitle}
          onChange={(e) => setFormTitle(e.target.value)}
          placeholder={autoTitle}
          className="w-full bg-white/10 border border-white/25 rounded-lg px-3 py-2 text-lg font-bold text-white placeholder-white/55 focus:outline-none focus:border-white/50"
        />
      ) : (
        <>
          <div className="text-[22px] font-bold leading-tight mb-1">{title}</div>
          <div className="text-[13.5px] text-white/85">
            {clientName}{claimTypeName ? ` · ${claimTypeName}` : ''} · Instructed {formatDateUK(instructedIso ? instructedIso.slice(0, 10) : null)}
          </div>
          {/* ★ STAGE PROGRESS STRIP — placeholder from derived status until
              Callum's real PI stages land (see file header). */}
          <div className="flex gap-1 mt-4">
            {STAGE_ORDER.map((s, i) => (
              <div key={s} className={`flex-1 h-1.5 rounded-full ${i < stageIdx ? 'bg-[#74c69d]' : i === stageIdx ? 'bg-white' : 'bg-white/20'}`} />
            ))}
          </div>
          <div className="text-[11px] text-white/80 mt-1.5">
            {STAGE_ORDER.map((s, i) => (
              <React.Fragment key={s}>
                {i > 0 && ' → '}
                {i === stageIdx ? <b>{STAGE_LABEL[s]}</b> : STAGE_LABEL[s]}
              </React.Fragment>
            ))}
            <span className="opacity-70"> · status (PI stages soon)</span>
          </div>
        </>
      )}
      </div>
    </div>
  )

  // ── Drill-in hero (D2): ← · case › Recent activity · ‹ n of N ›, then the
  // activity's own type tile / pills / title / who·when. Same band + ✕. ──
  const drillHero = (v: NonNullable<typeof drillView>, a: Activity, total: number): React.ReactNode => (
    <div className="relative px-6 sm:px-8 py-6 text-white bg-gradient-to-br from-casevo-forest to-casevo-brand dark:from-[#1a4230] dark:to-[#2d6a4f]">
      <button type="button" onClick={() => guardEdit(onClose)} aria-label="Close"
        className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors focus:outline-none casevo-focus">✕</button>
      <div data-drill-pane="">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 mb-3.5 pr-12 min-w-0 text-[12.5px] text-white/75">
          <button ref={drillBackRef} type="button" onClick={backToCase} aria-label="Back to case"
            className="shrink-0 w-7 h-7 rounded-full bg-white/15 hover:bg-white/[0.28] text-white grid place-items-center transition-colors focus:outline-none casevo-focus">
            <IcArrowLeft className="w-[15px] h-[15px]" />
          </button>
          <button type="button" onClick={backToCase} title="Back to case"
            className="min-w-0 truncate font-semibold text-white hover:underline focus:outline-none casevo-focus rounded-sm">{title}</button>
          <span aria-hidden="true" className="opacity-50">›</span>
          <span className="shrink-0">Recent activity</span>
          <span className="ml-auto shrink-0 inline-flex items-center gap-0.5 tabular-nums">
            <button type="button" onClick={() => stepDrill(-1)} disabled={drillIdx <= 0} aria-label="Newer entry" className={STEP_BTN}>
              <IcChevLeft className="w-3.5 h-3.5" />
            </button>
            <span className="px-1.5 text-[12px] text-white/85" aria-live="polite">{drillIdx + 1} of {total}</span>
            <button type="button" onClick={() => stepDrill(1)} disabled={drillIdx >= total - 1} aria-label="Older entry" className={STEP_BTN}>
              <IcChevRight className="w-3.5 h-3.5" />
            </button>
          </span>
        </nav>
        <div className="flex items-start gap-3">
          <span aria-hidden="true" className="shrink-0 w-9 h-9 -mt-0.5 rounded-[10px] bg-white/15 grid place-items-center">
            <EntryIconSvg icon={entryTypeFor(a.actionType).icon} size={17} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={HERO_PILL}>{v.eyebrow}</span>
              {v.pills}
            </div>
            <h2 className="text-[22px] font-bold leading-tight mb-1">{v.title}</h2>
            <div className="text-[13.5px] text-white/85">{v.subtitle}</div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
    {/* While the drill-in's Edit form is open the quick-view is HIDDEN (frame +
        backdrop) but stays MOUNTED, so the form is the only modal on screen and
        closing it lands back on the same activity detail. */}
    <div hidden={editingActivity !== null} className="fixed inset-0 z-[70] overflow-y-auto bg-black/40">
      <div className="flex min-h-full items-center justify-center p-4">
        <div ref={shellRef} className="w-full max-w-3xl rounded-2xl overflow-hidden bg-casevo-surface shadow-2xl">
          {drillView && drillActivity && recent ? drillHero(drillView, drillActivity, recent.length) : heroBand(view === 'edit')}
          {error && (
            <ErrorNote className="mx-6 sm:mx-8 mt-4">{error}</ErrorNote>
          )}
          {drillView ? (
            // ══════════════ DRILL-IN (activity detail, in place) ══════════════
            <>
              <div data-drill-pane="" className="px-6 sm:px-8 py-6 flex flex-col gap-[14px]">
                {drillView.body}
                {drillError && <div role="alert" className="text-[12.5px] text-casevo-danger">{drillError}</div>}
              </div>
              <div className="flex items-center px-6 sm:px-8 py-4 border-t border-casevo-border bg-casevo-input">
                <div data-drill-pane="" className="flex flex-1 min-w-0 items-center gap-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">{drillView.footerLeft}</div>
                  <div className="ml-auto flex items-center gap-2.5">{drillView.footerRight}</div>
                </div>
              </div>
            </>
          ) : view === 'view' ? (
            // ══════════════ VIEW MODE ══════════════
            <>
              <div data-drill-pane="">
              {/* Tabs — Overview is live; the section tabs are Stage-3 stubs. */}
              <div className="flex gap-6 px-6 sm:px-8 border-b border-casevo-border">
                <button type="button" className="py-3 text-[13px] font-semibold text-casevo-brand border-b-2 border-casevo-brand -mb-px">Overview</button>
                {['Parties', 'Injury', 'Medical', 'Witnesses'].map((t) => (
                  <button key={t} type="button" disabled title="Opens in the full case (coming soon)"
                    className="py-3 text-[13px] font-semibold text-casevo-mid/60 border-b-2 border-transparent cursor-not-allowed">{t}</button>
                ))}
              </div>
              <div className="px-6 sm:px-8 py-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                {/* Client */}
                <div>
                  <div className={bhCls}><IcUser />Client</div>
                  <div className="mb-3">
                    <div className={kLbl}>Name</div>
                    {clientId ? (
                      <button type="button" onClick={() => onOpenClient(caseSummary)}
                        className="text-sm font-semibold text-casevo-brand hover:text-casevo-forest hover:underline focus:outline-none casevo-focus">
                        {clientName} →
                      </button>
                    ) : <div className={kVal}>{clientName}</div>}
                  </div>
                  <div className="mb-3"><div className={kLbl}>Claim type</div><div className={kVal}>{claimTypeName ?? 'Not set'}</div></div>
                  <div><div className={kLbl}>Date of accident</div><div className={kVal}>{formatDateUK(accidentIso ? accidentIso.slice(0, 10) : null) || '—'}</div></div>
                </div>
                {/* Handling team */}
                <div>
                  <div className={bhCls}><IcUsers />Handling team</div>
                  <div className="mb-3">
                    <div className={kLbl}>Fee earner</div>
                    {assignee ? (
                      <span className="inline-flex items-center gap-2 bg-casevo-mint rounded-full pl-1 pr-3 py-1">
                        {/* The shared UserAvatar (as everywhere); the row's own look
                            only when the person isn't in the firm list. */}
                        {(() => {
                          const av = assignee as { avatarColour?: string; avatarStyle?: 'solid' | 'soft' | 'icon'; avatarPhotoUrl?: string | null }
                          return (
                            <UserAvatar userId={assignee.id} fallbackName={`${assignee.firstName} ${assignee.lastName}`.trim()}
                              fallbackLook={{ colour: av.avatarColour, style: av.avatarStyle, photoUrl: av.avatarPhotoUrl }} size="sm" title={false} />
                          )
                        })()}
                        <span className="text-[13px] font-medium text-casevo-forest">
                          {liveName(assignee.id, `${assignee.firstName} ${assignee.lastName}`)}{assignee.id === currentUserId ? ' (you)' : ''}
                        </span>
                      </span>
                    ) : <div className="text-sm text-casevo-mid italic">Unassigned</div>}
                  </div>
                  <div>
                    <div className={kLbl}>{teamFirmWide ? 'Visible to' : 'Team'}</div>
                    <AssigneeChips
                      firmWide={teamFirmWide}
                      userIds={teamIds.filter((id) => id !== (assignee?.id ?? caseSummary.assignedToUserId))}
                      firmUsers={firmUsers}
                      currentUserId={currentUserId}
                      emptyText="Just the fee earner."
                    />
                  </div>
                </div>
                {/* Defendant / TPI */}
                <div>
                  <div className={bhCls}><IcShield />Defendant / TPI</div>
                  <div className="mb-3"><div className={kLbl}>Defendant</div>{latePhase === 'ready' ? <div className={kVal}>{defendantName || '—'}</div> : <ValueSkel w="w-40" phase={latePhase} />}</div>
                  <div className="mb-3"><div className={kLbl}>TPI insurer</div>{latePhase === 'ready' ? <div className={kVal}>{full?.tpiInsurer?.trim() || '—'}</div> : <ValueSkel w="w-32" phase={latePhase} />}</div>
                  <div><div className={kLbl}>TPI reference</div>{latePhase === 'ready' ? <div className={kVal}>{full?.tpiReferenceNumber?.trim() || '—'}</div> : <ValueSkel w="w-24" phase={latePhase} />}</div>
                </div>
                {/* Key dates */}
                <div>
                  <div className={bhCls}><IcClock />Key dates</div>
                  <div className="mb-3"><div className={kLbl}>Limitation</div>{latePhase !== 'ready' || deadlines === null ? <ValueSkel w="w-28" phase={latePhase} /> : <><div className={`${kVal} flex items-center gap-[6px]`}>{limitationText}{limitationCheck?.kind === 'warn' && <LaterThanEstimateDot message={limitationCheck.message} />}</div>{limitationCheck?.kind === 'error' && <div role="alert" className="text-[12px] mt-[3px] leading-snug text-casevo-danger">{limitationCheck.message}</div>}{full?.piMatter?.protectedParty === true && <div role="status" className="text-[12px] mt-[3px] leading-snug text-casevo-warn">{LIMITATION_NOTES.capacity}</div>}</>}</div>
                  <div>
                    <div className={kLbl}>Next deadline</div>
                    {latePhase !== 'ready' || deadlines === null ? (
                      <ValueSkel w="w-44" phase={latePhase} />
                    ) : nextDeadline ? (
                      <div className={`text-sm font-semibold ${nextDeadlineUrgent ? 'text-red-600 dark:text-red-400' : 'text-casevo-forest'}`}>
                        {nextDeadline.deadlineType.name} · {formatDateUK(nextDeadline.dueDate.slice(0, 10))}
                      </div>
                    ) : <div className="text-sm text-casevo-mid">None upcoming</div>}
                  </div>
                </div>
                {/* Recent activity — full width, a timeline RAIL (thin line, a
                    type-coloured dot per entry, two tight lines). No £: a
                    glance; billing lives in the full feed. */}
                <div className="sm:col-span-2">
                  <div className={bhCls}><IcActivity />Recent activity</div>
                  {/* Always RECENT_ACTIVITY_LIMIT rows tall (skeleton, empty, error or
                      a short list), so the swap never changes the modal's height. */}
                  <div style={{ minHeight: RECENT_ACTIVITY_LIMIT * ACTIVITY_ROW_PX }}>
                  {latePhase !== 'ready' ? (
                    <ul aria-hidden="true" className={`flex flex-col ${latePhase === 'wait' ? 'invisible' : ''}`}>
                      {Array.from({ length: RECENT_ACTIVITY_LIMIT }, (_, i) => (
                        <li key={i} className={`${RAIL_ROW} pr-8 py-1`}>
                          <span className="absolute left-[7px] top-[8px] w-2.5 h-2.5 rounded-full bg-casevo-border-soft ring-[3px] ring-casevo-surface" />
                          <span className="block h-[18px] leading-[18px]"><span className={`${SKELETON_BAR} h-3 w-full max-w-[300px]`} /></span>
                          <span className="block h-[16px] leading-[16px]"><span className={`${SKELETON_BAR} h-2.5 w-36`} /></span>
                        </li>
                      ))}
                    </ul>
                  ) : recentError ? (
                    <div className="text-sm text-casevo-mid">Couldn’t load recent activity.</div>
                  ) : !recent || recent.length === 0 ? (
                    <div className="text-sm text-casevo-mid">No activity yet.</div>
                  ) : (
                    <ul className="flex flex-col">
                      {recent.map((a) => {
                        const def = entryTypeFor(a.actionType)
                        return (
                          <li
                            key={a.id}
                            ref={(el) => { if (el) rowRefs.current.set(a.id, el); else rowRefs.current.delete(a.id) }}
                            role="button"
                            tabIndex={0}
                            aria-label={`View ${a.action}`}
                            onClick={() => navigate(a.id, 1)}
                            onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); navigate(a.id, 1) } }}
                            className={`${RAIL_ROW} group pr-8 py-1 rounded-lg cursor-pointer ${RAIL_HOVER} focus:outline-none casevo-focus`}
                          >
                            {/* Dot: the type's tone (same source as the feed); the
                                inner bg-current paints the tone's text colour. The
                                ring cuts the rail and follows the hover tint. */}
                            <span aria-hidden="true" className={`absolute z-[2] left-[7px] top-[8px] w-2.5 h-2.5 rounded-full overflow-hidden ring-[3px] ring-casevo-surface group-hover:ring-casevo-input transition-colors ${TONES[resolveEntryTone(def.key, firmColourScheme, userColourScheme)]}`}>
                              <span className="block w-full h-full bg-current" />
                            </span>
                            <span className="relative z-[2] block truncate text-[13px] leading-[18px] font-medium text-casevo-forest" title={a.action}>{a.action}</span>
                            <span className="relative z-[2] block truncate text-[11.5px] leading-[16px] text-casevo-text-soft" title={new Date(a.createdAt).toLocaleString('en-GB')}>
                              <span className="font-semibold text-casevo-mid">{def.label}</span> · {liveName(a.actorUserId, a.actorName)} · {relativeTime(a.createdAt)}
                            </span>
                            {/* Drill-in hint: a chevron that nudges in on hover / keyboard focus. */}
                            <span aria-hidden="true" className="absolute z-[2] right-2.5 top-1/2 -translate-y-1/2 -translate-x-1 opacity-0 text-casevo-mid transition-[opacity,transform] group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100 group-focus-visible:translate-x-0">
                              <IcChevRight className="w-[15px] h-[15px]" />
                            </span>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                  </div>
                  {recentClientId && (
                    <button type="button" onClick={viewAllActivity}
                      className="mt-2 text-[12.5px] font-semibold text-casevo-brand hover:text-casevo-forest hover:underline focus:outline-none casevo-focus rounded-sm">
                      View all activity →
                    </button>
                  )}
                </div>
              </div>
              </div>
              {/* Footer */}
              <div className="flex items-center px-6 sm:px-8 py-4 border-t border-casevo-border bg-casevo-input">
                <div data-drill-pane="" className="flex flex-1 min-w-0 items-center gap-2.5">
                {canEdit && (
                  <button type="button" disabled={saving || statusBusy}
                    onClick={() => {
                      setStatusError(null)
                      if (stage === 'closed') { setStatusDialog('reopen'); return }
                      setCloseDateDraft(ymdLocal(new Date())) // LOCAL today
                      setCompleteOnClose([])
                      setStatusDialog('close')
                    }}
                    className="mr-auto px-4 py-2.5 rounded-lg border border-casevo-border text-sm font-semibold text-casevo-forest hover:border-casevo-mid disabled:opacity-50 focus:outline-none casevo-focus">
                    {stage === 'closed' ? 'Reopen…' : 'Close case…'}
                  </button>
                )}
                {statusError && <span role="alert" className="text-[12.5px] text-casevo-danger mr-2">{statusError}</span>}
                <button type="button" onClick={() => onOpenFull(caseSummary.id, editedRef.current ? null : full, deadlines)}
                  className={`${canEdit ? '' : 'mr-auto'} px-4 py-2.5 rounded-lg border border-casevo-border text-sm font-semibold text-casevo-forest hover:border-casevo-mid focus:outline-none casevo-focus`}>
                  Full case →
                </button>
                {canEdit && (
                  <button type="button" onClick={() => setView('edit')}
                    className={BTN_PRIMARY_MD}>Edit</button>
                )}
                </div>
              </div>
            </>
          ) : (
            // ══════════════ EDIT MODE ══════════════
            <>
              <div className="px-6 sm:px-8 py-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0">
                  <div className="mb-4">
                    <label className={lblCls}>Client</label>
                    <input className={`${inputCls} opacity-60 cursor-not-allowed`} value={clientName} disabled title="Edit the client on the full case page" />
                  </div>
                  <div className="mb-4">
                    <label className={lblCls}>Claim type</label>
                    <input className={`${inputCls} opacity-60 cursor-not-allowed`} value={claimTypeName ?? 'Not set'} disabled title="Change the claim type on the full case page" />
                  </div>
                  <div className="mb-4">
                    <label className={lblCls}>Stage</label>
                    <select className={inputCls} value={formStage} onChange={(e) => setFormStage(e.target.value as Stage)}>
                      {STAGE_ORDER.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className={lblCls}>Date of accident</label>
                    <DateInput value={formAccidentDate} onChange={setFormAccidentDate} ariaLabel="Date of accident" onValidityChange={(v) => setAccidentTyping(!v)} />
                    {accidentBlock
                      ? <p role="alert" className="text-[12px] text-casevo-danger mt-[5px]">{accidentBlock}</p>
                      : accidentWarn && <p role="status" className="text-[12px] text-casevo-warn mt-[5px]">{accidentWarn}</p>}
                  </div>
                  <div className="mb-4">
                    <label className={lblCls}>Fee earner</label>
                    <select className={inputCls} value={formAssignee} onChange={(e) => setFormAssignee(e.target.value)} disabled={!isAdmin}>
                      <option value="">Unassigned</option>
                      {firmUsers.map((u) => (
                        <option key={u.id} value={u.id}>{u.firstName} {u.lastName}{u.id === currentUserId ? ' (you)' : ''}</option>
                      ))}
                    </select>
                    {!isAdmin && <p className="text-[11px] text-casevo-mid mt-1">Only admins can reassign a case.</p>}
                  </div>
                  <div className="mb-4">
                    <label className={lblCls}>Defendant</label>
                    <input className={inputCls} value={formDefendant} onChange={(e) => setFormDefendant(e.target.value)} placeholder="Optional" />
                  </div>
                  {/* ★ Handling team — audience (Just me / Specific people /
                      Everyone), the appointment control. Gated by
                      assignCasesToOthers / createFirmWideCases; read-only chips
                      when neither is granted. Saved with the rest on Save. */}
                  <div className="mb-4 sm:col-span-2">
                    <AudienceControl
                      label="Assign to (handling team)"
                      noun="case"
                      audience={formAudience}
                      onAudience={setFormAudience}
                      selectedIds={formAudienceIds}
                      onSelectedIds={setFormAudienceIds}
                      canPeople={canAssignCasesToOthers}
                      canFirm={canCreateFirmWideCases}
                      firmUsers={firmUsers}
                      currentUserId={currentUserId}
                    />
                  </div>
                  <div className="mb-4 sm:col-span-2">
                    <label className={lblCls}>Limitation date</label>
                    <input className={`${inputCls} opacity-60 cursor-not-allowed`} value={limitationText} disabled title="Edit deadlines on the full case page" />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2.5 px-6 sm:px-8 py-4 border-t border-casevo-border bg-casevo-input">
                <button type="button" onClick={() => guardEdit(() => { setView('view'); setError(null) })} disabled={saving}
                  className="px-4 py-2.5 rounded-lg border border-casevo-border text-sm font-semibold text-casevo-mid hover:text-casevo-forest hover:border-casevo-mid disabled:opacity-50 focus:outline-none casevo-focus">Cancel</button>
                <button type="button" onClick={() => { void handleSave() }} disabled={saving || accidentTyping || !!accidentBlock}
                  className={BTN_PRIMARY_MD}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
      {/* Drill-in Edit / Delete — the case Activity section's wiring, rendered
          OUTSIDE the overlay so hiding it (above) never hides them. The edit
          form replaces the quick-view on screen; the delete confirm is a small
          dialog layered over it. ModalFrame returns focus to the drill-in's
          Edit / Delete button on close (the quick-view is visible again by then). */}
      {editingActivity && recentClientId && (
        <AddActivityModal
          isOpen={true}
          onClose={() => setEditingActivity(null)}
          clientId={recentClientId}
          clientDisplayName={clientName}
          cases={[{ id: caseSummary.id, accidentDate: accidentIso ?? null, closeDate, setupFinalised, updatedAt: caseSummary.updatedAt, ref, name: title }]}
          existingActivity={editingActivity}
          onCreated={() => { setEditingActivity(null); setRecentReload((n) => n + 1) }}
        />
      )}
      {/* Close case / Reopen — the footer's status action. */}
      <ConfirmDialogue
        isOpen={statusDialog === 'close'}
        title="Close case"
        message="It's marked Closed on the date below and leaves the Open case list (find it under the Closed filter; reopen it any time). Its deadline and task alerts stop — except limitation deadlines."
        confirmLabel={statusBusy ? 'Closing…' : 'Close case'}
        tone="primary"
        confirmDisabled={!closeDateDraft || statusBusy || closeTyping || !!closeBlock}
        onCancel={() => { if (!statusBusy) setStatusDialog(null) }}
        onConfirm={() => { void runStatus('close') }}
      >
        <p className="block text-[12px] font-semibold text-casevo-forest mb-[5px]">Close date</p>
        <DateInput value={closeDateDraft} onChange={setCloseDateDraft} ariaLabel="Close date" onValidityChange={(v) => setCloseTyping(!v)} />
        {closeBlock && <p role="alert" className="text-[12px] text-casevo-danger mt-[5px]">{closeBlock}</p>}
        {openDeadlinesForClose.length > 0 && (
          <fieldset className="mt-[14px]">
            <legend className="block text-[12px] font-semibold text-casevo-forest mb-[5px]">Open deadlines — tick any to mark complete now</legend>
            <ul className="flex flex-col gap-[6px] max-h-[220px] overflow-y-auto">
              {openDeadlinesForClose.map((d) => (
                <li key={d.id} className={`rounded-[8px] border px-[10px] py-[7px] ${d.isLimitation ? 'border-casevo-warn-border bg-casevo-warn-bg' : 'border-casevo-border-soft'}`}>
                  <label className="flex items-start gap-[8px] text-[13px] text-casevo-forest cursor-pointer">
                    <input type="checkbox" className="mt-[3px] accent-casevo-primary" disabled={statusBusy}
                      checked={completeOnClose.includes(d.id)}
                      onChange={(e) => setCompleteOnClose((ids) => (e.target.checked ? [...ids, d.id] : ids.filter((x) => x !== d.id)))} />
                    <span className="min-w-0">
                      <span className="font-medium">{d.name}</span> <span className="text-casevo-text-soft">· due {formatDateUK(d.dueDate)}</span>
                      {d.note && <span className="block text-[12px] text-casevo-warn mt-[2px]">{d.note}</span>}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}
      </ConfirmDialogue>
      <ConfirmDialogue
        isOpen={statusDialog === 'reopen'}
        title="Reopen this case?"
        message="This clears its close date, so it shows as an open case again."
        confirmLabel={statusBusy ? 'Reopening…' : 'Reopen case'}
        tone="primary"
        confirmDisabled={statusBusy}
        onCancel={() => { if (!statusBusy) setStatusDialog(null) }}
        onConfirm={() => { void runStatus('reopen') }}
      />
      <ConfirmDialogue
        isOpen={confirmDeleteActivity !== null}
        title="Delete activity"
        message={confirmDeleteActivity ? `Delete this activity entry (${confirmDeleteActivity.action})? This can't be undone.` : ''}
        confirmLabel={deletingActivity ? 'Deleting...' : 'Delete'}
        onConfirm={() => { void handleConfirmDeleteActivity() }}
        onCancel={() => setConfirmDeleteActivity(null)}
      />
    </>
  )
}

export default CaseQuickViewModal
